/**
 * The Bundles card list of the customer and property pages (tracker item 85):
 * one card per active (or pending) enrollment, collapsed to name, progress and
 * next visit, expandable to the timeline, plus the "Add service bundle"
 * wizard. Office roles only (the enrollment routes are ADMIN / OFFICE_MANAGER).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAPIClient, IBundlesApi, IUsersApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { formatJobMoney } from '../../utils/jobPresentation';
import { primaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { AddBundleWizard } from './AddBundleWizard';
import { EnrollmentTimeline, ProgressBar } from './EnrollmentTimeline';
import { enrollmentPath, enrollmentStatusClasses, enrollmentStatusLabel, nextVisitText, progressText } from './bundleUi';

type SectionApi = IBundlesApi & Pick<IUsersApi, 'listAssignableUsers'>;

interface SectionProps {
  customerId: string;
  /** Property detail page: only this property's enrollments */
  propertyId?: string;
  properties: Pick<types.Property, 'id' | 'address' | 'city' | 'state'>[];
  canManage: boolean;
}

function BundleCard({ enrollment, showProperty, technicians }: { enrollment: types.BundleEnrollment; showProperty: boolean; technicians: Record<string, string> }) {
  const [open, setOpen] = useState(false);
  const next = nextVisitText(enrollment.nextVisitDate);
  const pending = enrollment.status === 'PENDING_APPROVAL';
  return (
    <li className="rounded-lg border border-gray-200 p-3 sm:p-4" data-testid="bundle-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 font-semibold text-gray-900">
            <Link to={enrollmentPath(enrollment.id)} className="text-blue-700 hover:underline break-words">{enrollment.bundleName}</Link>
            {pending && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${enrollmentStatusClasses(enrollment.status)}`}>{enrollmentStatusLabel(enrollment.status)}</span>}
          </p>
          {showProperty && <p className="text-sm text-gray-600 break-words">{enrollment.property.address}</p>}
          <p className="mt-1 text-sm text-gray-600">
            {pending ? 'Waiting for approval' : progressText(enrollment.progress)}
            {next && <span> · {next}</span>}
            <span> · {formatJobMoney(enrollment.total)}</span>
          </p>
        </div>
        <button type="button" className="min-h-[44px] px-2 text-sm font-semibold text-blue-700 hover:underline sm:min-h-0" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide visits' : 'Show visits'}
        </button>
      </div>
      {!pending && <div className="mt-2"><ProgressBar progress={enrollment.progress} label={progressText(enrollment.progress)} /></div>}
      {open && <div className="mt-4"><EnrollmentTimeline enrollment={enrollment} technicians={technicians} showNext={false} /></div>}
    </li>
  );
}

export function BundlesSection({ customerId, propertyId, properties, canManage }: SectionProps) {
  const api = useMemo(() => createAPIClient() as SectionApi, []);
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState<types.BundleEnrollment[] | null>(null);
  const [technicians, setTechnicians] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [wizard, setWizard] = useState(false);

  const load = useCallback(() => {
    let active = true;
    const request = propertyId ? api.listPropertyEnrollments(propertyId, { limit: 50 }) : api.listEnrollments({ customerId, limit: 50 });
    request
      .then((page) => { if (active) { setEnrollments(page.data.filter((e) => e.status === 'ACTIVE' || e.status === 'PENDING_APPROVAL')); setError(null); } })
      .catch((err) => { if (active) { setEnrollments([]); setError(getErrorMessage(err, 'Could not load the service bundles')); } });
    return () => { active = false; };
  }, [api, customerId, propertyId]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    let active = true;
    api.listAssignableUsers()
      .then((users) => { if (active) setTechnicians(Object.fromEntries(users.map((u) => [u.id, u.email]))); })
      .catch(() => undefined);
    return () => { active = false; };
  }, [api]);

  const canAdd = canManage && (Boolean(propertyId) || properties.length > 0);

  return (
    <section aria-labelledby="bundles-heading" className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 id="bundles-heading" className="text-sm font-semibold uppercase tracking-wide text-gray-900">
          Service bundles {enrollments && enrollments.length > 0 && <span className="font-normal text-gray-400">({enrollments.length})</span>}
        </h2>
        {canAdd && <button type="button" className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`} onClick={() => setWizard(true)}>Add service bundle</button>}
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {enrollments === null ? (
        <p className="text-sm text-gray-500">Loading service bundles...</p>
      ) : enrollments.length === 0 ? (
        <p className="text-sm text-gray-500">{error ? 'Service bundles could not be loaded.' : propertyId ? 'No active service bundles at this property.' : 'No active service bundles for this customer.'}</p>
      ) : (
        <ul className="space-y-3">
          {enrollments.map((enrollment) => <BundleCard key={enrollment.id} enrollment={enrollment} showProperty={!propertyId} technicians={technicians} />)}
        </ul>
      )}
      {wizard && (
        <AddBundleWizard
          propertyId={propertyId}
          properties={properties}
          onClose={() => setWizard(false)}
          onAdded={(created) => { setWizard(false); navigate(enrollmentPath(created.id)); }}
        />
      )}
    </section>
  );
}
